import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Play } from 'lucide-react';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { useAuth } from '../auth/AuthContext';
import { resolveReportFilters, URL_STATUS_LABELS } from '../services/accuracyEvaluationScope';
import api from '../services/api';
import {
  createBaselineReport as createBaselineReportApi,
} from '../services/accuracyBackendApi';
import type { UrlStatus } from '../types/accuracy';
import './AccuracyReportPage.css';
import './AccuracyReportRunPage.css';

const DEFAULT_URL_STATUSES: UrlStatus[] = ['reviewed'];

const ACCURACY_RUN_STATUSES: UrlStatus[] = ['reviewed', 'review_pending'];

function parseList(sp: URLSearchParams, key: string): string[] {
  const raw = sp.get(key);
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function displayUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname + (u.pathname !== '/' ? u.pathname : '');
  } catch {
    return url.length > 64 ? `${url.slice(0, 64)}…` : url;
  }
}

const AccuracyReportRunPage: React.FC = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [jobOptions, setJobOptions] = useState<{ label: string; value: string; guideline_id?: string; version?: number }[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<string[]>(() => parseList(searchParams, 'job_ids'));
  const [selectedStatuses, setSelectedStatuses] = useState<UrlStatus[]>(() => {
    const v = parseList(searchParams, 'url_statuses') as UrlStatus[];
    return v.length ? v : DEFAULT_URL_STATUSES;
  });
  const [reportName, setReportName] = useState('');
  const [computing, setComputing] = useState(false);
  const [guidelineId, setGuidelineId] = useState('');
  const [guidelineVersion, setGuidelineVersion] = useState(0);
  const [urlPreviewLoading, setUrlPreviewLoading] = useState(false);
  const [urlPreviewError, setUrlPreviewError] = useState<string | null>(null);
  const [urlPreviewTotal, setUrlPreviewTotal] = useState<number>(0);
  const [urlPreviewRows, setUrlPreviewRows] = useState<any[]>([]);

  useEffect(() => {
    setReportName((prev) => prev || `評価レポート ${new Date().toLocaleDateString('ja-JP')}`);
  }, []);

  useEffect(() => {
    setJobsLoading(true);
    setJobsError(null);
    (async () => {
      try {
        const res: any = await api.jobs.getFilterOptions();
        const jobs: any[] = res?.data?.jobs || [];
        const options = jobs.map((j) => {
          const gid = j.guideline_id || '';
          const ver = typeof j.version === 'number' ? j.version : Number(j.version || 0);
          const suffix = gid && ver ? `（${gid.slice(0, 8)}… v${ver}）` : '';
          return {
            label: `${j.job_name || j.id}${suffix}`,
            value: j.id,
            guideline_id: gid || undefined,
            version: ver || undefined,
          };
        });
        setJobOptions(options);
      } catch (e) {
        setJobsError(e instanceof Error ? e.message : 'ジョブ一覧の取得に失敗しました');
      } finally {
        setJobsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const selected = jobOptions.filter((o) => selectedJobs.includes(o.value));
    const gids = Array.from(new Set(selected.map((s) => s.guideline_id).filter(Boolean)));
    const vers = Array.from(new Set(selected.map((s) => s.version).filter(Boolean)));
    if (gids.length === 1 && vers.length === 1) {
      setGuidelineId(gids[0] as string);
      setGuidelineVersion(vers[0] as number);
    } else {
      setGuidelineId('');
      setGuidelineVersion(0);
    }
  }, [selectedJobs, jobOptions]);

  const statusOptions = useMemo(
    () =>
      ACCURACY_RUN_STATUSES.map((s) => ({
        label: URL_STATUS_LABELS[s],
        value: s,
      })),
    []
  );

  const filters = useMemo(() => {
    return resolveReportFilters({
      selectedJobIds: selectedJobs,
      selectedStatuses,
      allJobIds: selectedJobs,
      guideline_id: guidelineId,
      guideline_version: guidelineVersion,
    });
  }, [selectedJobs, selectedStatuses, guidelineId, guidelineVersion]);

  useEffect(() => {
    if (selectedJobs.length === 0 || selectedStatuses.length === 0) {
      setUrlPreviewRows([]);
      setUrlPreviewTotal(0);
      return;
    }
    setUrlPreviewLoading(true);
    setUrlPreviewError(null);
    (async () => {
      try {
        const res: any = await api.jobs.getUrlProcessResults(
          1,
          50,
          undefined,
          selectedJobs,
          selectedStatuses,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        );
        const rows = res?.data || [];
        const total = res?.pagination?.totalItems ?? rows.length;
        setUrlPreviewRows(rows);
        setUrlPreviewTotal(total);
      } catch (e) {
        setUrlPreviewError(e instanceof Error ? e.message : 'URL プレビューの取得に失敗しました');
        setUrlPreviewRows([]);
        setUrlPreviewTotal(0);
      } finally {
        setUrlPreviewLoading(false);
      }
    })();
  }, [selectedJobs, selectedStatuses]);

  const canCreate =
    guidelineId.trim().length > 0 &&
    guidelineVersion > 0 &&
    selectedJobs.length > 0 &&
    !computing &&
    !jobsLoading;

  const handleRunReport = async () => {
    if (!canCreate) return;

    const name =
      reportName.trim() || `評価レポート ${new Date().toLocaleString('ja-JP')}`;

    setComputing(true);
    try {
      const queued = await createBaselineReportApi({
        name,
        filters,
      });
      navigate(`/accuracy/reports/${encodeURIComponent(queued.id)}`, { replace: true });
    } finally {
      setComputing(false);
    }
  };

  return (
    <div className="accuracy-page accuracy-run-page">
      <div className="accuracy-run-breadcrumb">
        <Link to="/accuracy" className="accuracy-link accuracy-run-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
      </div>

      <div className="accuracy-header">
        <div className="accuracy-title">
          <h1>レポートを作成</h1>
          <p>評価条件を入力して評価レポートを作成します（URL プレビューは backend API 実装後に復活）。</p>
        </div>
      </div>

      <section className="accuracy-card accuracy-run-section" aria-labelledby="accuracy-run-conditions">
        <h2 id="accuracy-run-conditions" className="accuracy-run-section-title">
          1. 評価条件
        </h2>
        <div className="accuracy-filters accuracy-run-filters">
          <MultiSelectFilter
            label="ジョブ"
            options={jobOptions.map((o) => ({ label: o.label, value: o.value }))}
            selectedValues={selectedJobs}
            onSelectionChange={setSelectedJobs}
            placeholder="ジョブを選択"
            loading={jobsLoading}
          />
          <MultiSelectFilter
            label="URLステータス"
            options={statusOptions}
            selectedValues={selectedStatuses}
            onSelectionChange={(v) => setSelectedStatuses(v as UrlStatus[])}
          />
          <label className="accuracy-run-name-field">
            <span className="accuracy-run-name-label">レポート名</span>
            <input
              type="text"
              className="accuracy-run-name-input"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="例: 【ニコス】5月度③"
            />
          </label>
        </div>

        <dl className="accuracy-run-meta">
          <div>
            <dt>ガイドライン</dt>
            <dd>{guidelineId ? guidelineId : '—'}</dd>
          </div>
          <div>
            <dt>バージョン</dt>
            <dd>{guidelineVersion ? `v${guidelineVersion}` : '—'}</dd>
          </div>
          <div>
            <dt>対象ジョブ</dt>
            <dd>{selectedJobs.length}件</dd>
          </div>
          <div>
            <dt>URLステータス</dt>
            <dd>{selectedStatuses.map((s) => URL_STATUS_LABELS[s]).join('、')}</dd>
          </div>
        </dl>
        <p className="accuracy-run-hint">
          同じガイドライン・同じバージョンのジョブのみ同時に評価できます。
        </p>
        {jobsError && (
          <div className="accuracy-card accuracy-warnings" style={{ marginTop: 12 }}>
            {jobsError}
          </div>
        )}
        {selectedJobs.length > 0 && (!guidelineId || !guidelineVersion) && (
          <div className="accuracy-card accuracy-warnings" style={{ marginTop: 12 }}>
            異なるガイドライン/バージョンのジョブが混在しています。同じ組み合わせのジョブのみ選択してください。
          </div>
        )}
      </section>

      <section className="accuracy-card accuracy-run-section" aria-labelledby="accuracy-run-urls">
        <div className="accuracy-run-urls-header">
          <h2 id="accuracy-run-urls" className="accuracy-run-section-title">
            2. 評価対象 URL
          </h2>
          <span className="accuracy-run-count-badge">
            {urlPreviewLoading ? '読み込み中…' : `${urlPreviewTotal} 件が評価に含まれます`}
          </span>
        </div>

        {urlPreviewError && (
          <div className="accuracy-card accuracy-warnings" style={{ marginTop: 12 }}>
            {urlPreviewError}
          </div>
        )}

        {!urlPreviewLoading && urlPreviewRows.length === 0 ? (
          <div className="accuracy-empty accuracy-run-empty">
            <p>条件に一致する URL がありません。</p>
            <p className="accuracy-run-hint">
              ジョブまたは URL ステータスを変更してください。
            </p>
          </div>
        ) : (
          <div className="accuracy-run-url-table-wrap">
            <table className="accuracy-table accuracy-run-url-table">
              <thead>
                <tr>
                  <th>ジョブ</th>
                  <th>URL</th>
                  <th>ステータス</th>
                  <th>存在カテゴリ</th>
                  <th>違反数</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {urlPreviewRows.map((row: any) => {
                  const existenceDict = row.category_content_existence_dict || {};
                  const existenceCount = Object.values(existenceDict).filter(Boolean).length;
                  const violationCount = row.violation_count ?? 0;
                  return (
                    <tr key={`${row.job_id}-${row.id}`}>
                      <td>{row.job_name || row.job_id}</td>
                      <td className="accuracy-run-url-cell">
                        <span title={row.url}>{displayUrl(row.url)}</span>
                        <span className="accuracy-run-url-full">{row.url}</span>
                      </td>
                      <td>
                        <span className="accuracy-run-status-pill">
                          {URL_STATUS_LABELS[row.status as UrlStatus] || row.status}
                        </span>
                      </td>
                      <td>{existenceCount}</td>
                      <td>{violationCount}</td>
                      <td>
                        <Link
                          className="accuracy-link"
                          to={`/violation/${row.job_id}/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {urlPreviewTotal > urlPreviewRows.length && (
              <p className="accuracy-run-hint" style={{ marginTop: 10 }}>
                最初の {urlPreviewRows.length} 件のみ表示しています。
              </p>
            )}
          </div>
        )}
      </section>

      <section className="accuracy-card accuracy-run-section accuracy-run-actions-section">
        <h2 className="accuracy-run-section-title">3. 実行</h2>
        <p className="accuracy-run-actions-desc">
          指定したジョブ/URL ステータスについて、存在フラグと違反検出の精度を計算します。
          （ガイドライン: {guidelineId} v{guidelineVersion || '—'}）
        </p>
        <div className="accuracy-run-actions">
          <Link to="/accuracy" className="accuracy-button">
            キャンセル
          </Link>
          <button
            type="button"
            className="accuracy-button primary"
            onClick={handleRunReport}
            disabled={!canCreate}
          >
            {computing ? (
              <Loader2 size={16} className="spin" style={{ marginRight: 6 }} />
            ) : (
              <Play size={16} style={{ marginRight: 6 }} />
            )}
            レポートを作成
          </button>
        </div>
      </section>
    </div>
  );
};

export default AccuracyReportRunPage;
